import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import HomeLayout from "../../asset/layout/HomeLayout";
import CustomButton from "../../components/mui/CustomButton";
import IOSSwitch from "../../style/globalStyles";
import {
  DEFAULT_ADMIN_SETTINGS,
  loadAdminSettings,
  saveAdminSettings,
} from "../../utils/adminSettings";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: "24px",
      border: "1px solid",
      borderColor: alpha("#11409B", 0.08),
      bgcolor: "#fff",
      boxShadow: "0 4px 32px rgba(15, 23, 42, 0.06)",
      overflow: "hidden",
      ...sx,
    }}
  >
    {children}
  </Card>
);

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    bgcolor: "#fff",
    "& fieldset": { borderColor: alpha("#11409B", 0.2) },
    "&:hover fieldset": { borderColor: "#11409B" },
  },
};

const SettingRow = ({ title, description, control }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    alignItems={{ xs: "flex-start", sm: "center" }}
    justifyContent="space-between"
    spacing={2}
    sx={{
      py: 2,
      borderBottom: `1px solid ${alpha("#11409B", 0.06)}`,
      "&:last-child": { borderBottom: "none", pb: 0 },
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 600, color: "#0f172a" }}>{title}</Typography>
      {description && (
        <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
          {description}
        </Typography>
      )}
    </Box>
    <Box sx={{ flexShrink: 0 }}>{control}</Box>
  </Stack>
);

const OptionCard = ({ selected, title, subtitle, onClick, accent = "#11409B" }) => (
  <Box
    onClick={onClick}
    sx={{
      p: 2,
      borderRadius: "16px",
      cursor: "pointer",
      border: "2px solid",
      borderColor: selected ? accent : alpha("#11409B", 0.12),
      bgcolor: selected ? alpha(accent, 0.06) : "#fff",
      transition: "all 0.2s",
      "&:hover": {
        borderColor: accent,
        bgcolor: alpha(accent, 0.04),
      },
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Box>
        <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>{title}</Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: "#64748b" }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {selected && (
        <Chip
          label="Active"
          size="small"
          sx={{
            bgcolor: alpha(accent, 0.12),
            color: accent,
            fontWeight: 700,
            fontSize: "0.65rem",
          }}
        />
      )}
    </Stack>
  </Box>
);

const TAB_CONFIG = [
  { id: 0, label: "Giftcard", icon: CardGiftcardOutlinedIcon },
  { id: 1, label: "General", icon: TuneOutlinedIcon },
  { id: 2, label: "Notifications", icon: NotificationsOutlinedIcon },
  { id: 3, label: "Payments", icon: PaymentOutlinedIcon },
  { id: 4, label: "Security", icon: SecurityOutlinedIcon },
];

export default function Settings() {
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState(() => loadAdminSettings());
  const [dirty, setDirty] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    setSettings(loadAdminSettings());
  }, []);

  const patch = (section, key, value) => {
    setSettings((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }));
    setDirty(true);
  };

  const handleSave = () => {
    saveAdminSettings(settings);
    setDirty(false);
    enqueueSnackbar("Settings saved successfully", { variant: "success" });
  };

  const handleReset = () => {
    setSettings({ ...DEFAULT_ADMIN_SETTINGS });
    setDirty(true);
    enqueueSnackbar("Settings reset to defaults — click Save to apply", {
      variant: "info",
    });
  };

  const premiumButtonSx = {
    height: "44px",
    borderRadius: "12px",
    fontWeight: 600,
    textTransform: "none",
    background: "linear-gradient(135deg, #11409B 0%, #2563eb 100%)",
    boxShadow: "0 6px 24px rgba(37, 99, 235, 0.35)",
    "&:hover": {
      background: "linear-gradient(135deg, #0f3580 0%, #1d4ed8 100%)",
    },
  };

  return (
    <HomeLayout>
      <Box
        sx={{
          minHeight: "100%",
          background:
            "linear-gradient(180deg, #f0f4fa 0%, #f5f7fa 35%, #eef2f7 100%)",
          mx: -3,
          mt: -3,
          px: 3,
          pt: 3,
          pb: 4,
        }}
      >
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              borderRadius: "24px",
              background:
                "linear-gradient(135deg, #0c3483 0%, #11409B 40%, #1e40af 70%, #2563eb 100%)",
              color: "#fff",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(17, 64, 155, 0.35)",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={2}
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <SettingsOutlinedIcon />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, letterSpacing: "0.1em", opacity: 0.85 }}
                  >
                    CONFIGURATION
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, letterSpacing: "-0.03em" }}
                >
                  Settings
                </Typography>
                <Typography sx={{ mt: 1, opacity: 0.82, maxWidth: 520 }}>
                  Manage giftcard providers, platform defaults, notifications,
                  payments, and security preferences.
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <CustomButton
                  variant="contained"
                  onClick={handleReset}
                  startIcon={<RestartAltOutlinedIcon />}
                  sx={{
                    ...premiumButtonSx,
                    bgcolor: alpha("#fff", 0.12),
                    boxShadow: "none",
                    border: `1px solid ${alpha("#fff", 0.25)}`,
                    "&:hover": { bgcolor: alpha("#fff", 0.2) },
                  }}
                >
                  Reset defaults
                </CustomButton>
                <CustomButton
                  variant="contained"
                  onClick={handleSave}
                  disabled={!dirty}
                  startIcon={<SaveOutlinedIcon />}
                  sx={{
                    ...premiumButtonSx,
                    bgcolor: alpha("#fff", 0.15),
                    backdropFilter: "blur(8px)",
                    border: `1px solid ${alpha("#fff", 0.25)}`,
                    boxShadow: "none",
                    "&:hover": { bgcolor: alpha("#fff", 0.22) },
                    "&.Mui-disabled": { opacity: 0.5, color: "#fff" },
                  }}
                >
                  Save changes
                </CustomButton>
              </Stack>
            </Stack>
          </Box>
        </motion.div>

        <motion.div custom={1} initial="hidden" animate="visible" variants={fadeUp}>
          <PanelCard sx={{ mb: 3 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 1,
                minHeight: 52,
                borderBottom: `1px solid ${alpha("#11409B", 0.08)}`,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  color: "#64748b",
                  minHeight: 52,
                  gap: 1,
                },
                "& .Mui-selected": { color: "#11409B" },
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                  bgcolor: "#11409B",
                },
              }}
            >
              {TAB_CONFIG.map(({ id, label, icon: Icon }) => (
                <Tab
                  key={id}
                  label={label}
                  icon={<Icon sx={{ fontSize: 20 }} />}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </PanelCard>
        </motion.div>

        <motion.div custom={2} initial="hidden" animate="visible" variants={fadeUp}>
          {tab === 0 && (
            <PanelCard sx={{ p: { xs: 2, md: 3 } }}>
              <Typography sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}>
                Giftcard environment
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mb: 2.5 }}>
                Choose whether giftcards use mock data or live fulfillment.
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <OptionCard
                    selected={settings.giftcard.mode === "dummy"}
                    title="Dummy"
                    subtitle="Mock responses — safe for staging & QA"
                    accent="#d97706"
                    onClick={() => patch("giftcard", "mode", "dummy")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <OptionCard
                    selected={settings.giftcard.mode === "real"}
                    title="Real"
                    subtitle="Live giftcard issuance via selected API"
                    accent="#059669"
                    onClick={() => patch("giftcard", "mode", "real")}
                  />
                </Grid>
              </Grid>

              <Typography sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}>
                Giftcard API provider
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mb: 2.5 }}>
                Select which vendor powers giftcard catalog and fulfillment.
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <OptionCard
                    selected={settings.giftcard.apiProvider === "WOOHOO"}
                    title="WOOHOO"
                    subtitle="Third-party Woohoo giftcard integration"
                    accent="#2563eb"
                    onClick={() => patch("giftcard", "apiProvider", "WOOHOO")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <OptionCard
                    selected={settings.giftcard.apiProvider === "BUDGETREE"}
                    title="Budgetree"
                    subtitle="In-house Budgetree giftcard API"
                    accent="#7c3aed"
                    onClick={() => patch("giftcard", "apiProvider", "BUDGETREE")}
                  />
                </Grid>
              </Grid>

              <Typography sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}>
                Additional giftcard options
              </Typography>
              <SettingRow
                title="Sandbox mode"
                description="Use provider sandbox endpoints when available"
                control={
                  <FormControlLabel
                    control={
                      <IOSSwitch
                        checked={settings.giftcard.sandboxMode}
                        onChange={(e) =>
                          patch("giftcard", "sandboxMode", e.target.checked)
                        }
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                }
              />
              <SettingRow
                title="Auto fulfillment"
                description="Automatically fulfill giftcard orders after payment"
                control={
                  <FormControlLabel
                    control={
                      <IOSSwitch
                        checked={settings.giftcard.autoFulfillment}
                        onChange={(e) =>
                          patch("giftcard", "autoFulfillment", e.target.checked)
                        }
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                }
              />
              <SettingRow
                title="Log API requests"
                description="Store giftcard API request/response logs for debugging"
                control={
                  <FormControlLabel
                    control={
                      <IOSSwitch
                        checked={settings.giftcard.logApiRequests}
                        onChange={(e) =>
                          patch("giftcard", "logApiRequests", e.target.checked)
                        }
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                }
              />

              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: "12px",
                  bgcolor: alpha("#11409B", 0.04),
                  border: `1px solid ${alpha("#11409B", 0.08)}`,
                }}
              >
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                  Current configuration
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
                  <Chip
                    label={`Mode: ${settings.giftcard.mode}`}
                    size="small"
                    sx={{ fontWeight: 600, textTransform: "capitalize" }}
                  />
                  <Chip
                    label={`API: ${settings.giftcard.apiProvider}`}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  {settings.giftcard.sandboxMode && (
                    <Chip label="Sandbox" size="small" color="warning" variant="outlined" />
                  )}
                </Stack>
              </Box>
            </PanelCard>
          )}

          {tab === 1 && (
            <PanelCard sx={{ p: { xs: 2, md: 3 } }}>
              <SettingRow
                title="Maintenance mode"
                description="Show maintenance banner and block new checkouts"
                control={
                  <FormControlLabel
                    control={
                      <IOSSwitch
                        checked={settings.general.maintenanceMode}
                        onChange={(e) =>
                          patch("general", "maintenanceMode", e.target.checked)
                        }
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                }
              />
              <SettingRow
                title="Allow new registrations"
                description="Let new users sign up on the platform"
                control={
                  <FormControlLabel
                    control={
                      <IOSSwitch
                        checked={settings.general.allowNewRegistrations}
                        onChange={(e) =>
                          patch("general", "allowNewRegistrations", e.target.checked)
                        }
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                }
              />
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth sx={fieldSx}>
                    <InputLabel>Default currency</InputLabel>
                    <Select
                      value={settings.general.defaultCurrency}
                      label="Default currency"
                      onChange={(e) =>
                        patch("general", "defaultCurrency", e.target.value)
                      }
                    >
                      <MenuItem value="INR">INR — Indian Rupee</MenuItem>
                      <MenuItem value="USD">USD — US Dollar</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Support email"
                    value={settings.general.supportEmail}
                    onChange={(e) => patch("general", "supportEmail", e.target.value)}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Support phone"
                    value={settings.general.supportPhone}
                    onChange={(e) => patch("general", "supportPhone", e.target.value)}
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>
            </PanelCard>
          )}

          {tab === 2 && (
            <PanelCard sx={{ p: { xs: 2, md: 3 } }}>
              {[
                ["emailAlerts", "Email alerts", "Send admin email notifications"],
                ["orderAlerts", "Order alerts", "Notify on new orders"],
                ["lowStockAlerts", "Low stock alerts", "Alert when brand stock is low"],
                ["rewardAlerts", "Reward alerts", "Notify on reward point grants"],
              ].map(([key, title, description]) => (
                <SettingRow
                  key={key}
                  title={title}
                  description={description}
                  control={
                    <FormControlLabel
                      control={
                        <IOSSwitch
                          checked={settings.notifications[key]}
                          onChange={(e) =>
                            patch("notifications", key, e.target.checked)
                          }
                        />
                      }
                      label=""
                      sx={{ m: 0 }}
                    />
                  }
                />
              ))}
            </PanelCard>
          )}

          {tab === 3 && (
            <PanelCard sx={{ p: { xs: 2, md: 3 } }}>
              <SettingRow
                title="Payment test mode"
                description="Process payments in test/sandbox environment"
                control={
                  <FormControlLabel
                    control={
                      <IOSSwitch
                        checked={settings.payments.testMode}
                        onChange={(e) =>
                          patch("payments", "testMode", e.target.checked)
                        }
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                }
              />
              <SettingRow
                title="UPI payments"
                description="Allow customers to pay via UPI"
                control={
                  <FormControlLabel
                    control={
                      <IOSSwitch
                        checked={settings.payments.enableUpi}
                        onChange={(e) =>
                          patch("payments", "enableUpi", e.target.checked)
                        }
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                }
              />
              <SettingRow
                title="Card payments"
                description="Allow debit and credit card payments"
                control={
                  <FormControlLabel
                    control={
                      <IOSSwitch
                        checked={settings.payments.enableCards}
                        onChange={(e) =>
                          patch("payments", "enableCards", e.target.checked)
                        }
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                }
              />
              <SettingRow
                title="Net banking"
                description="Enable net banking payment option"
                control={
                  <FormControlLabel
                    control={
                      <IOSSwitch
                        checked={settings.payments.enableNetBanking}
                        onChange={(e) =>
                          patch("payments", "enableNetBanking", e.target.checked)
                        }
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                }
              />
            </PanelCard>
          )}

          {tab === 4 && (
            <PanelCard sx={{ p: { xs: 2, md: 3 } }}>
              <SettingRow
                title="Require OTP for admin login"
                description="Enforce OTP verification on admin sign-in"
                control={
                  <FormControlLabel
                    control={
                      <IOSSwitch
                        checked={settings.security.requireOtpForAdmin}
                        onChange={(e) =>
                          patch("security", "requireOtpForAdmin", e.target.checked)
                        }
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                }
              />
              <Box sx={{ mt: 2, maxWidth: 280 }}>
                <FormControl fullWidth sx={fieldSx}>
                  <InputLabel>Session timeout (minutes)</InputLabel>
                  <Select
                    value={settings.security.sessionTimeoutMinutes}
                    label="Session timeout (minutes)"
                    onChange={(e) =>
                      patch("security", "sessionTimeoutMinutes", e.target.value)
                    }
                  >
                    <MenuItem value={30}>30 minutes</MenuItem>
                    <MenuItem value={60}>60 minutes</MenuItem>
                    <MenuItem value={120}>2 hours</MenuItem>
                    <MenuItem value={480}>8 hours</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </PanelCard>
          )}
        </motion.div>

        {dirty && (
          <motion.div custom={3} initial="hidden" animate="visible" variants={fadeUp}>
            <PanelCard
              sx={{
                mt: 3,
                p: 2,
                bgcolor: alpha("#d97706", 0.06),
                borderColor: alpha("#d97706", 0.2),
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Typography sx={{ color: "#92400e", fontWeight: 600 }}>
                  You have unsaved changes
                </Typography>
                <CustomButton variant="contained" onClick={handleSave} sx={premiumButtonSx}>
                  Save now
                </CustomButton>
              </Stack>
            </PanelCard>
          </motion.div>
        )}
      </Box>
    </HomeLayout>
  );
}
